import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useR2Upload } from '@/hooks/useR2Upload';
import { compressImagesSerial, compressImage } from '@/lib/imageCompression';
import { uploadToR2 } from '@/lib/r2Upload';
import { useAdmin } from '@/context/AdminContext';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/SEOHead';
import SortableCategoryList from './components/SortableCategoryList';
import SortableAlbumList from './components/SortableAlbumList';
import SortablePhotoGrid from './components/SortablePhotoGrid';

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Album {
  id: number;
  category_id: number;
  title: string;
  description: string | null;
  cover_image: string | null;
  sort_order: number;
}

interface Photo {
  id: number;
  album_id: number;
  url: string;
  caption: string | null;
  sort_order: number;
}

interface HomeService {
  id: number;
  title: string;
  category_slug: string;
  description: string;
  image_key: string;
}

interface UploadQueueItem {
  file: File;
  id: string;
  status: 'pending' | 'compressing' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  url?: string;
}

interface HomePreviewImage {
  id: number;
  src: string;
  alt: string;
  category: string;
}

const HOME_PREVIEW_STORAGE_KEY = 'jb_home_preview_config';
const HOME_PREVIEW_LS_PREFIX = 'jb_img_portfolio-preview-';

const defaultHomeImages: HomePreviewImage[] = [
  { id: 1, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/0dec238de7d5fe8f14936e40452e79c1.jpeg', alt: 'Noiva se preparando', category: 'Casamentos' },
  { id: 2, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/c518aad8a7a36dc8f90eec59138d7b83.jpeg', alt: 'Ensaio pré-wedding', category: 'Pré-Wedding' },
  { id: 3, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/68c3cbd9ecdc4d5edfb5413f51ced623.jpeg', alt: 'Cerimônia ao ar livre', category: 'Casamentos' },
  { id: 4, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/91f097dd19a0cfabdf20ce4586f555f7.jpeg', alt: 'Batizado', category: 'Sessão Família' },
  { id: 5, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/dc3ad3e14674b83b587c9f0d8d1eff79.jpeg', alt: 'Ensaio pré-wedding no jardim', category: 'Pré-Wedding' },
  { id: 6, src: 'https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/38338eac109b9dcdef050a97d67ba2ee.jpeg', alt: 'Sessão família no parque', category: 'Sessão Família' },
];

function loadHomeImages(): HomePreviewImage[] {
  try {
    const raw = localStorage.getItem(HOME_PREVIEW_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as HomePreviewImage[];
      if (Array.isArray(parsed) && parsed.length === 6) return parsed;
    }
  } catch { /* ignore */ }
  return defaultHomeImages.map((img) => {
    try {
      const stored = localStorage.getItem(`${HOME_PREVIEW_LS_PREFIX}${img.id}`);
      return stored ? { ...img, src: stored } : img;
    } catch {
      return img;
    }
  });
}

export default function AdminPortfolio() {
  const navigate = useNavigate();
  const { upload } = useR2Upload();
  const { isAdmin, isLoading, logout } = useAdmin();

  const [activeTab, setActiveTab] = useState<'portfolio' | 'home-preview' | 'home-services'>('portfolio');

  // Portfolio state
  const [categories, setCategories] = useState<Category[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);

  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [useCompression, setUseCompression] = useState(false);

  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [creatingAlbum, setCreatingAlbum] = useState(false);

  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySlug, setNewCategorySlug] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Album editing state
  const [editingAlbum, setEditingAlbum] = useState(false);
  const [editAlbumTitle, setEditAlbumTitle] = useState('');
  const [editAlbumDescription, setEditAlbumDescription] = useState('');
  const [savingAlbum, setSavingAlbum] = useState(false);

  // Home section state
  const [homeImages, setHomeImages] = useState<HomePreviewImage[]>(loadHomeImages);
  const [homeUploadingId, setHomeUploadingId] = useState<number | null>(null);

  const [homeServices, setHomeServices] = useState<HomeService[]>([]);
  const [homeServicesLoading, setHomeServicesLoading] = useState(false);
  const [homeServiceUploadingId, setHomeServiceUploadingId] = useState<number | null>(null);

  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(new Set());
  const [selectMode, setSelectMode] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, isLoading, navigate]);

  useEffect(() => {
    if (activeTab === 'home-services') {
      fetchHomeServices();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'portfolio') {
      fetchCategories();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedCategory && activeTab === 'portfolio') {
      fetchAlbums(selectedCategory);
    } else if (activeTab === 'portfolio') {
      setAlbums([]);
      setSelectedAlbum(null);
    }
  }, [selectedCategory, activeTab]);

  useEffect(() => {
    if (selectedAlbum && activeTab === 'portfolio') {
      fetchPhotos(selectedAlbum.id);
    } else if (activeTab === 'portfolio') {
      setPhotos([]);
    }
  }, [selectedAlbum, activeTab]);

  // Sync editing fields when album changes
  useEffect(() => {
    if (selectedAlbum) {
      setEditAlbumTitle(selectedAlbum.title);
      setEditAlbumDescription(selectedAlbum.description || '');
    }
  }, [selectedAlbum]);

  async function fetchCategories() {
    const { data } = await supabase.from('portfolio_categories').select('*').order('sort_order');
    if (data) setCategories(data);
  }

  async function fetchAlbums(categoryId: number) {
    const { data } = await supabase
      .from('portfolio_albums')
      .select('*')
      .eq('category_id', categoryId)
      .order('sort_order');
    if (data) setAlbums(data);
  }

  async function fetchPhotos(albumId: number) {
    const { data } = await supabase
      .from('portfolio_photos')
      .select('*')
      .eq('album_id', albumId)
      .order('sort_order');
    if (data) setPhotos(data);
  }

  async function fetchHomeServices() {
    setHomeServicesLoading(true);
    const { data } = await supabase
      .from('home_services')
      .select('*')
      .order('sort_order');
    if (data) setHomeServices(data);
    setHomeServicesLoading(false);
  }

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  }

  function togglePhotoSelection(photoId: number) {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  }

  function selectAllPhotos() {
    setSelectedPhotoIds(new Set(photos.map((p) => p.id)));
  }

  function clearPhotoSelection() {
    setSelectedPhotoIds(new Set());
    setSelectMode(false);
  }

  async function deleteSelectedPhotos() {
    if (!selectedAlbum) return;
    const ids = Array.from(selectedPhotoIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Tem certeza que deseja remover ${ids.length} foto(s) do álbum?`)) return;

    const { error } = await supabase.from('portfolio_photos').delete().in('id', ids);
    if (error) {
      showNotification('error', 'Erro ao remover fotos: ' + error.message);
    } else {
      showNotification('success', `${ids.length} foto(s) removida(s).`);
      setSelectedPhotoIds(new Set());
      setSelectMode(false);
      fetchPhotos(selectedAlbum.id);
    }
  }

  async function updateAlbumInfo() {
    if (!selectedAlbum) return;
    setSavingAlbum(true);
    const { error } = await supabase
      .from('portfolio_albums')
      .update({
        title: editAlbumTitle.trim(),
        description: editAlbumDescription.trim() || null,
      })
      .eq('id', selectedAlbum.id);

    setSavingAlbum(false);
    if (error) {
      showNotification('error', 'Erro ao salvar álbum: ' + error.message);
    } else {
      showNotification('success', 'Informações do álbum atualizadas!');
      setSelectedAlbum((prev) => prev ? { ...prev, title: editAlbumTitle.trim(), description: editAlbumDescription.trim() || null } : null);
      setAlbums((prev) => prev.map((a) => a.id === selectedAlbum.id ? { ...a, title: editAlbumTitle.trim(), description: editAlbumDescription.trim() || null } : a));
      setEditingAlbum(false);
    }
  }

  async function setAlbumCover(photoUrl: string) {
    if (!selectedAlbum) return;
    const { error } = await supabase
      .from('portfolio_albums')
      .update({ cover_image: photoUrl })
      .eq('id', selectedAlbum.id);
    if (error) {
      showNotification('error', 'Erro ao definir capa: ' + error.message);
    } else {
      showNotification('success', 'Capa do álbum atualizada!');
      setSelectedAlbum((prev) => prev ? { ...prev, cover_image: photoUrl } : null);
      setAlbums((prev) => prev.map((a) => a.id === selectedAlbum.id ? { ...a, cover_image: photoUrl } : a));
    }
  }

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || !selectedAlbum) return;

    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      showNotification('error', 'Selecione apenas arquivos de imagem.');
      return;
    }

    if (imageFiles.length > 100) {
      showNotification('error', 'Limite de 100 fotos por vez. Selecione menos arquivos.');
      return;
    }

    let filesToUpload = imageFiles;

    if (useCompression) {
      setQueue((prev) => [
        ...prev,
        ...imageFiles.map((file) => ({
          file,
          id: Math.random().toString(36).slice(2),
          status: 'compressing' as const,
          progress: 5,
        })),
      ]);

      try {
        filesToUpload = await compressImagesSerial(
          imageFiles,
          { maxWidth: 1600, maxHeight: 1600, quality: 0.8, mimeType: 'image/jpeg' },
          (current, total, fileName) => {
            setQueue((prev) =>
              prev.map((q) =>
                q.file.name === fileName && q.status === 'compressing'
                  ? { ...q, progress: Math.round((current / total) * 30) }
                  : q
              )
            );
          }
        );
      } catch {
        // fall back to original files
      }

      setQueue((prev) =>
        prev.map((q) =>
          q.status === 'compressing' ? { ...q, status: 'pending' as const, progress: 30 } : q
        )
      );
    } else {
      const newItems: UploadQueueItem[] = imageFiles.map((file) => ({
        file,
        id: Math.random().toString(36).slice(2),
        status: 'pending',
        progress: 15,
      }));
      setQueue((prev) => [...prev, ...newItems]);
    }
  }, [selectedAlbum, useCompression]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  async function processQueue() {
    if (isUploading || queue.length === 0 || !selectedAlbum) return;
    setIsUploading(true);

    const pending = queue.filter((item) => item.status === 'pending');

    for (let i = 0; i < pending.length; i++) {
      const item = pending[i];
      setQueue((prev) =>
        prev.map((q) => (q.id === item.id ? { ...q, status: 'uploading', progress: 35 } : q))
      );

      try {
        if (i > 0) await new Promise((r) => setTimeout(r, 200));

        const result = await upload(item.file, 'portfolio');

        setQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, progress: 75 } : q))
        );

        const { error } = await supabase.from('portfolio_photos').insert({
          album_id: selectedAlbum.id,
          url: result.url,
          caption: item.file.name.replace(/\.[^/.]+$/, ''),
          sort_order: photos.length + i + 1,
        });

        if (error) throw error;

        setQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? { ...q, status: 'success', progress: 100, url: result.url }
              : q
          )
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro no upload';
        setQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: 'error', error: message } : q))
        );
      }
    }

    await fetchPhotos(selectedAlbum.id);
    setIsUploading(false);

    const errors = queue.filter((q) => q.status === 'error');
    const successes = queue.filter((q) => q.status === 'success');

    if (errors.length === 0 && successes.length > 0) {
      setShowSuccessPopup(true);
      setQueue([]);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
    } else if (errors.length > 0) {
      showNotification('error', `${errors.length} upload(s) falharam. Verifique os itens abaixo.`);
    }
  }

  useEffect(() => {
    if (!isUploading && queue.some((q) => q.status === 'pending')) {
      processQueue();
    }
  }, [queue, isUploading]);

  async function createAlbum() {
    if (!selectedCategory || !newAlbumTitle.trim()) return;
    setCreatingAlbum(true);

    const { data, error } = await supabase
      .from('portfolio_albums')
      .insert({
        category_id: selectedCategory,
        title: newAlbumTitle.trim(),
        description: newAlbumDescription.trim() || null,
        cover_image: null,
        sort_order: albums.length + 1,
      })
      .select()
      .single();

    if (error) {
      showNotification('error', 'Erro ao criar álbum: ' + error.message);
    } else if (data) {
      showNotification('success', 'Álbum criado com sucesso!');
      setAlbums((prev) => [...prev, data]);
      setSelectedAlbum(data);
      setShowCreateAlbum(false);
      setNewAlbumTitle('');
      setNewAlbumDescription('');
    }

    setCreatingAlbum(false);
  }

  async function createCategory() {
    if (!newCategoryName.trim() || !newCategorySlug.trim()) return;
    setCreatingCategory(true);

    const slug = newCategorySlug.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const { data, error } = await supabase
      .from('portfolio_categories')
      .insert({
        name: newCategoryName.trim(),
        slug,
        sort_order: categories.length + 1,
      })
      .select()
      .single();

    if (error) {
      showNotification('error', 'Erro ao criar categoria: ' + error.message);
    } else if (data) {
      showNotification('success', 'Categoria criada com sucesso!');
      setCategories((prev) => [...prev, data]);
      setSelectedCategory(data.id);
      setShowCreateCategory(false);
      setNewCategoryName('');
      setNewCategorySlug('');
    }

    setCreatingCategory(false);
  }

  async function deletePhoto(photoId: number) {
    if (!selectedAlbum) return;
    if (!window.confirm('Tem certeza que deseja remover esta foto do álbum?')) return;

    const { error } = await supabase.from('portfolio_photos').delete().eq('id', photoId);
    if (error) {
      showNotification('error', 'Erro ao remover foto.');
    } else {
      showNotification('success', 'Foto removida.');
      fetchPhotos(selectedAlbum.id);
    }
  }

  async function deleteAlbum(albumId: number) {
    if (!window.confirm('Tem certeza que deseja excluir este álbum e todas as suas fotos?')) return;

    const { error } = await supabase.from('portfolio_albums').delete().eq('id', albumId);
    if (error) {
      showNotification('error', 'Erro ao excluir álbum.');
    } else {
      showNotification('success', 'Álbum excluído.');
      setAlbums((prev) => prev.filter((a) => a.id !== albumId));
      if (selectedAlbum?.id === albumId) setSelectedAlbum(null);
    }
  }

  const removeFromQueue = (id: string) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  // --- Drag & Drop reorder handlers ---
  async function reorderCategories(newCategories: Category[]) {
    setCategories(newCategories);
    for (let i = 0; i < newCategories.length; i++) {
      await supabase
        .from('portfolio_categories')
        .update({ sort_order: i + 1 })
        .eq('id', newCategories[i].id);
    }
    showNotification('success', 'Ordem das categorias atualizada!');
  }

  async function reorderAlbums(newAlbums: Album[]) {
    setAlbums(newAlbums);
    for (let i = 0; i < newAlbums.length; i++) {
      await supabase
        .from('portfolio_albums')
        .update({ sort_order: i + 1 })
        .eq('id', newAlbums[i].id);
    }
    showNotification('success', 'Ordem dos álbuns atualizada!');
  }

  async function reorderPhotos(newPhotos: Photo[]) {
    setPhotos(newPhotos);
    for (let i = 0; i < newPhotos.length; i++) {
      await supabase
        .from('portfolio_photos')
        .update({ sort_order: i + 1 })
        .eq('id', newPhotos[i].id);
    }
    showNotification('success', 'Ordem das fotos atualizada!');
  }

  async function moveAlbumToCategory(albumId: number, newCategoryId: number) {
    const { error } = await supabase
      .from('portfolio_albums')
      .update({ category_id: newCategoryId, sort_order: 999 })
      .eq('id', albumId);
    if (error) {
      showNotification('error', 'Erro ao mover álbum: ' + error.message);
    } else {
      showNotification('success', 'Álbum movido para nova categoria!');
      if (selectedCategory === newCategoryId) {
        fetchAlbums(newCategoryId);
      } else {
        setAlbums((prev) => prev.filter((a) => a.id !== albumId));
        if (selectedAlbum?.id === albumId) setSelectedAlbum(null);
      }
    }
  }

  async function movePhotoToAlbum(photoId: number, newAlbumId: number) {
    const { error } = await supabase
      .from('portfolio_photos')
      .update({ album_id: newAlbumId, sort_order: 999 })
      .eq('id', photoId);
    if (error) {
      showNotification('error', 'Erro ao mover foto: ' + error.message);
    } else {
      showNotification('success', 'Foto movida para outro álbum!');
      if (selectedAlbum) {
        fetchPhotos(selectedAlbum.id);
      }
    }
  }

  // Home section handlers
  async function handleHomeImageUpload(id: number, file: File) {
    setHomeUploadingId(id);
    try {
      const compressed = await compressImage(file, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.85,
        mimeType: 'image/jpeg',
      });
      const result = await uploadToR2(compressed, 'site-images');
      setHomeImages((prev) => prev.map((img) => img.id === id ? { ...img, src: result.url } : img));
      // Also update localStorage for EditableImage compatibility
      localStorage.setItem(`${HOME_PREVIEW_LS_PREFIX}${id}`, result.url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro no upload';
      showNotification('error', `Erro ao enviar imagem ${id}: ${msg}`);
    } finally {
      setHomeUploadingId(null);
    }
  }

  function updateHomeField(id: number, field: 'alt' | 'category', value: string) {
    setHomeImages((prev) => prev.map((img) => img.id === id ? { ...img, [field]: value } : img));
  }

  function saveHomeSection() {
    try {
      localStorage.setItem(HOME_PREVIEW_STORAGE_KEY, JSON.stringify(homeImages));
      // Also save each src to EditableImage keys for consistency
      homeImages.forEach((img) => {
        localStorage.setItem(`${HOME_PREVIEW_LS_PREFIX}${img.id}`, img.src);
      });
      showNotification('success', 'Seção inicial salva com sucesso! Recarregue a home para ver as mudanças.');
    } catch {
      showNotification('error', 'Erro ao salvar configurações da home.');
    }
  }

  async function handleHomeServiceUpload(id: number, file: File) {
    setHomeServiceUploadingId(id);
    try {
      const compressed = await compressImage(file, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.85,
        mimeType: 'image/jpeg',
      });
      const result = await uploadToR2(compressed, 'site-images');

      const service = homeServices.find((s) => s.id === id);
      if (service) {
        await supabase
          .from('site_images')
          .upsert(
            { image_key: service.image_key, url: result.url, updated_at: new Date().toISOString() },
            { onConflict: 'image_key' }
          );
        localStorage.setItem(`jb_img_${service.image_key}`, result.url);
      }

      showNotification('success', 'Imagem do serviço atualizada!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro no upload';
      showNotification('error', `Erro ao enviar imagem: ${msg}`);
    } finally {
      setHomeServiceUploadingId(null);
    }
  }

  function updateHomeServiceField(id: number, field: 'title' | 'category_slug' | 'description', value: string) {
    setHomeServices((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
  }

  async function saveHomeServices() {
    for (const service of homeServices) {
      await supabase
        .from('home_services')
        .update({
          title: service.title.trim(),
          category_slug: service.category_slug.trim(),
          description: service.description.trim(),
        })
        .eq('id', service.id);
    }
    showNotification('success', 'Serviços da home salvos com sucesso!');
  }

  function resetHomeSection() {
    if (!window.confirm('Tem certeza que deseja restaurar as imagens originais da home?')) return;
    localStorage.removeItem(HOME_PREVIEW_STORAGE_KEY);
    defaultHomeImages.forEach((img) => {
      localStorage.removeItem(`${HOME_PREVIEW_LS_PREFIX}${img.id}`);
    });
    setHomeImages(defaultHomeImages);
    showNotification('success', 'Imagens originais restauradas. Recarregue a home para ver as mudanças.');
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-dark/50">Redirecionando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Gerenciar Portfólio | Jéssica Bonini Fotógrafa"
        description="Painel de gerenciamento de portfólio. Faça upload de fotos, crie álbuns e organize seu portfólio."
        canonicalPath="/admin/portfolio"
        noindex
      />
      <Navbar />

      <main className="pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-dark font-normal">Gerenciar Portfólio</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/portfolio')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-full text-sm font-medium hover:bg-dark/90 transition-colors whitespace-nowrap"
              >
                <i className="ri-eye-line" />
                Ver Portfólio
              </button>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-red-200 text-red-600 rounded-full text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line" />
                Sair
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-8 border-b border-warm-200 pb-1">
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'portfolio'
                  ? 'text-dark border-b-2 border-dark'
                  : 'text-dark/50 hover:text-dark'
              }`}
            >
              Portfólio e Álbuns
            </button>
            <button
              onClick={() => setActiveTab('home-preview')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'home-preview'
                  ? 'text-dark border-b-2 border-dark'
                  : 'text-dark/50 hover:text-dark'
              }`}
            >
              Seção Inicial — Registros Selecionados
            </button>
            <button
              onClick={() => setActiveTab('home-services')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'home-services'
                  ? 'text-dark border-b-2 border-dark'
                  : 'text-dark/50 hover:text-dark'
              }`}
            >
              Serviços — Home
            </button>
          </div>

          {notification && (
            <div
              className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${
                notification.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {notification.message}
            </div>
          )}

          {activeTab === 'portfolio' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <div className="bg-cream rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold tracking-wider uppercase text-dark/50">Categoria</h2>
                    <button
                      onClick={() => setShowCreateCategory(true)}
                      className="text-xs font-medium text-dark/70 hover:text-dark flex items-center gap-1 transition-colors"
                    >
                      <i className="ri-add-line" />
                      Nova
                    </button>
                  </div>

                  {showCreateCategory && (
                    <div className="bg-white rounded-lg p-4 mb-4 border border-warm-200">
                      <input
                        type="text"
                        placeholder="Nome da categoria"
                        value={newCategoryName}
                        onChange={(e) => {
                          setNewCategoryName(e.target.value);
                          setNewCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                        }}
                        className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40 mb-2"
                      />
                      <input
                        type="text"
                        placeholder="Slug (ex: casamentos)"
                        value={newCategorySlug}
                        onChange={(e) => setNewCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                        className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40 mb-3"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={createCategory}
                          disabled={!newCategoryName.trim() || !newCategorySlug.trim() || creatingCategory}
                          className="flex-1 px-3 py-2 bg-dark text-white rounded-lg text-xs font-medium hover:bg-dark/90 disabled:opacity-40 transition-colors"
                        >
                          {creatingCategory ? 'Criando...' : 'Criar'}
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateCategory(false);
                            setNewCategoryName('');
                            setNewCategorySlug('');
                          }}
                          className="px-3 py-2 bg-white text-dark border border-warm-200 rounded-lg text-xs font-medium hover:bg-warm-100 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  <SortableCategoryList
                    categories={categories}
                    selectedId={selectedCategory}
                    onSelect={setSelectedCategory}
                    onReorder={reorderCategories}
                  />
                </div>

                <div className="bg-cream rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold tracking-wider uppercase text-dark/50">Álbuns</h2>
                    {selectedCategory && (
                      <button
                        onClick={() => setShowCreateAlbum(true)}
                        className="text-xs font-medium text-dark/70 hover:text-dark flex items-center gap-1 transition-colors"
                      >
                        <i className="ri-add-line" />
                        Novo
                      </button>
                    )}
                  </div>

                  {showCreateAlbum && selectedCategory && (
                    <div className="bg-white rounded-lg p-4 mb-4 border border-warm-200">
                      <input
                        type="text"
                        placeholder="Nome do álbum"
                        value={newAlbumTitle}
                        onChange={(e) => setNewAlbumTitle(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40 mb-2"
                      />
                      <textarea
                        placeholder="Descrição (opcional)"
                        value={newAlbumDescription}
                        onChange={(e) => setNewAlbumDescription(e.target.value)}
                        rows={2}
                        maxLength={300}
                        className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40 mb-3 resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={createAlbum}
                          disabled={!newAlbumTitle.trim() || creatingAlbum}
                          className="flex-1 px-3 py-2 bg-dark text-white rounded-lg text-xs font-medium hover:bg-dark/90 disabled:opacity-40 transition-colors"
                        >
                          {creatingAlbum ? 'Criando...' : 'Criar'}
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateAlbum(false);
                            setNewAlbumTitle('');
                            setNewAlbumDescription('');
                          }}
                          className="px-3 py-2 bg-white text-dark border border-warm-200 rounded-lg text-xs font-medium hover:bg-warm-100 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {albums.length === 0 ? (
                    <p className="text-dark/40 text-sm text-center py-6">
                      {selectedCategory ? 'Nenhum álbum nesta categoria.' : 'Selecione uma categoria.'}
                    </p>
                  ) : (
                    <SortableAlbumList
                      albums={albums}
                      selectedId={selectedAlbum?.id ?? null}
                      onSelect={setSelectedAlbum}
                      onDelete={deleteAlbum}
                      onReorder={reorderAlbums}
                      categories={categories}
                      onMoveCategory={moveAlbumToCategory}
                    />
                  )}
                </div>

                <div className="bg-cream rounded-xl p-5">
                  <h2 className="text-sm font-semibold tracking-wider uppercase text-dark/50 mb-4">Álbum Selecionado</h2>
                  {selectedAlbum ? (
                    <div className="bg-white rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          {editingAlbum ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editAlbumTitle}
                                onChange={(e) => setEditAlbumTitle(e.target.value)}
                                className="w-full px-2 py-1.5 rounded border border-warm-200 text-sm text-dark focus:outline-none focus:border-dark/40"
                              />
                              <textarea
                                value={editAlbumDescription}
                                onChange={(e) => setEditAlbumDescription(e.target.value)}
                                rows={2}
                                maxLength={300}
                                className="w-full px-2 py-1.5 rounded border border-warm-200 text-xs text-dark focus:outline-none focus:border-dark/40 resize-none"
                                placeholder="Descrição do álbum"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={updateAlbumInfo}
                                  disabled={savingAlbum || !editAlbumTitle.trim()}
                                  className="px-3 py-1.5 bg-dark text-white rounded text-xs font-medium hover:bg-dark/90 disabled:opacity-40 transition-colors"
                                >
                                  {savingAlbum ? 'Salvando...' : 'Salvar'}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingAlbum(false);
                                    setEditAlbumTitle(selectedAlbum.title);
                                    setEditAlbumDescription(selectedAlbum.description || '');
                                  }}
                                  className="px-3 py-1.5 bg-white text-dark border border-warm-200 rounded text-xs font-medium hover:bg-warm-100 transition-colors"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <h3 className="font-serif text-lg text-dark mb-1">{selectedAlbum.title}</h3>
                              <p className="text-dark/50 text-xs mb-3">{selectedAlbum.description || 'Sem descrição'}</p>
                            </>
                          )}
                        </div>
                        {!editingAlbum && (
                          <button
                            onClick={() => setEditingAlbum(true)}
                            className="text-dark/40 hover:text-dark transition-colors"
                            title="Editar álbum"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-dark/50">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-image-line" />
                          {photos.length} fotos
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-folder-line" />
                          {categories.find((c) => c.id === selectedAlbum.category_id)?.name}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-dark/40 text-sm text-center py-6">Selecione um álbum para gerenciar.</p>
                  )}
                </div>
              </div>

              {selectedAlbum && (
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold tracking-wider uppercase text-dark/50">Upload de Fotos</h2>
                    <label className="flex items-center gap-2 text-xs text-dark/60 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={useCompression}
                        onChange={(e) => setUseCompression(e.target.checked)}
                        className="w-4 h-4 accent-dark"
                      />
                      Comprimir antes de enviar (mais lento, arquivo menor)
                    </label>
                  </div>

                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl p-8 md:p-12 text-center transition-colors ${
                      dragOver ? 'border-dark bg-cream' : 'border-warm-300 bg-cream/50'
                    }`}
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-warm-100 mx-auto mb-4">
                      <i className="ri-upload-cloud-2-line text-2xl text-dark/40" />
                    </div>
                    <p className="text-dark/70 text-sm font-medium mb-1">
                      Arraste e solte fotos aqui, ou
                    </p>
                    <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-full text-sm font-medium hover:bg-dark/90 transition-colors cursor-pointer mt-2">
                      <i className="ri-folder-open-line" />
                      Selecionar Arquivos
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                      />
                    </label>
                    <p className="text-dark/30 text-xs mt-3">
                      Imagens JPG, PNG, WEBP. Máximo 100 arquivos por vez.
                      {useCompression
                        ? ' As fotos serão comprimidas antes do envio.'
                        : ' Upload direto — mais rápido, sem compressão.'}
                    </p>
                  </div>

                  {queue.length > 0 && (
                    <div className="mt-6 bg-cream rounded-xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-dark">Fila de Upload ({queue.length})</h3>
                        <button
                          onClick={() => setQueue([])}
                          className="text-xs text-dark/50 hover:text-dark transition-colors"
                        >
                          Limpar fila
                        </button>
                      </div>
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {queue.map((item) => (
                          <div key={item.id} className="bg-white rounded-lg p-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-warm-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {item.status === 'success' && item.url ? (
                                <img src={item.url} alt="" className="w-full h-full object-cover" />
                              ) : item.status === 'compressing' ? (
                                <i className="ri-loader-4-line text-dark/30 animate-spin" />
                              ) : (
                                <i className="ri-image-line text-dark/30" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-dark truncate">{item.file.name}</p>
                                {item.status === 'compressing' && (
                                  <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">comprimindo</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-warm-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-300 ${
                                      item.status === 'error'
                                        ? 'bg-red-400'
                                        : item.status === 'success'
                                          ? 'bg-emerald-400'
                                          : 'bg-dark'
                                    }`}
                                    style={{ width: `${item.progress}%` }}
                                  />
                                </div>
                                <span className="text-[10px] text-dark/40 w-10 text-right">
                                  {item.status === 'success'
                                    ? 'OK'
                                    : item.status === 'error'
                                      ? 'Erro'
                                      : item.status === 'compressing'
                                        ? `${item.progress}%`
                                        : `${item.progress}%`}
                                </span>
                              </div>
                              {item.error && (
                                <p className="text-[10px] text-red-500 mt-1">{item.error}</p>
                              )}
                            </div>
                            <button
                              onClick={() => removeFromQueue(item.id)}
                              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-dark/40 hover:text-red-500 transition-colors flex-shrink-0"
                            >
                              <i className="ri-close-line text-sm" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedAlbum && photos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-semibold tracking-wider uppercase text-dark/50">Fotos do Álbum</h2>
                      <button
                        onClick={() => setSelectMode((prev) => !prev)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${selectMode ? 'bg-dark text-white' : 'bg-cream text-dark/60 hover:bg-warm-200'}`}
                      >
                        {selectMode ? 'Cancelar seleção' : 'Selecionar'}
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectMode && (
                        <>
                          <span className="text-xs text-dark/50">{selectedPhotoIds.size} selecionada(s)</span>
                          <button
                            onClick={selectAllPhotos}
                            className="text-xs font-medium text-dark/60 hover:text-dark transition-colors"
                          >
                            Selecionar todas
                          </button>
                          <button
                            onClick={clearPhotoSelection}
                            className="text-xs font-medium text-dark/60 hover:text-dark transition-colors"
                          >
                            Limpar
                          </button>
                          {selectedPhotoIds.size > 0 && (
                            <button
                              onClick={deleteSelectedPhotos}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-medium hover:bg-red-100 transition-colors"
                            >
                              <i className="ri-delete-bin-line" />
                              Excluir {selectedPhotoIds.size}
                            </button>
                          )}
                        </>
                      )}
                      <span className="text-xs text-dark/40">{photos.length} foto(s)</span>
                    </div>
                  </div>
                  <SortablePhotoGrid
                    photos={photos}
                    selectMode={selectMode}
                    selectedIds={selectedPhotoIds}
                    onToggleSelect={togglePhotoSelection}
                    onDelete={deletePhoto}
                    onSetCover={setAlbumCover}
                    onReorder={reorderPhotos}
                    albums={albums}
                    onMoveAlbum={movePhotoToAlbum}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === 'home-preview' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-serif text-xl md:text-2xl text-dark">Registros Selecionados — Home</h2>
                  <p className="text-dark/50 text-sm mt-1">
                    Edite as 6 imagens que aparecem na seção "Registros selecionados" da página inicial.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveHomeSection}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-full text-sm font-medium hover:bg-dark/90 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-save-line" />
                    Salvar alterações
                  </button>
                  <button
                    onClick={resetHomeSection}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-warm-200 text-dark rounded-full text-sm font-medium hover:bg-warm-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    Restaurar originais
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {homeImages.map((img) => (
                  <div key={img.id} className="bg-cream rounded-xl p-4">
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-warm-100 mb-4">
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                      {homeUploadingId === img.id && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-dark/60 mb-1">Imagem</label>
                        <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-warm-200 rounded-lg text-xs font-medium text-dark cursor-pointer hover:bg-warm-100 transition-colors">
                          <i className="ri-upload-2-line" />
                          Trocar imagem
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleHomeImageUpload(img.id, file);
                            }}
                          />
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-dark/60 mb-1">Texto alternativo (alt)</label>
                        <input
                          type="text"
                          value={img.alt}
                          onChange={(e) => updateHomeField(img.id, 'alt', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-dark/60 mb-1">Categoria exibida</label>
                        <input
                          type="text"
                          value={img.category}
                          onChange={(e) => updateHomeField(img.id, 'category', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'home-services' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-serif text-xl md:text-2xl text-dark">Serviços — Home</h2>
                  <p className="text-dark/50 text-sm mt-1">
                    Edite os 4 cards de serviço da seção "A Fotografia que encanta".
                  </p>
                </div>
                <button
                  onClick={saveHomeServices}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark text-white rounded-full text-sm font-medium hover:bg-dark/90 transition-colors whitespace-nowrap"
                >
                  <i className="ri-save-line" />
                  Salvar alterações
                </button>
              </div>

              {homeServicesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {homeServices.map((service) => (
                    <div key={service.id} className="bg-cream rounded-xl p-4">
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-warm-100 mb-4">
                        <img
                          src={(() => {
                            const local = localStorage.getItem(`jb_img_${service.image_key}`);
                            return local || '';
                          })()}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                        {homeServiceUploadingId === service.id && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-dark/60 mb-1">Imagem</label>
                          <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-warm-200 rounded-lg text-xs font-medium text-dark cursor-pointer hover:bg-warm-100 transition-colors">
                            <i className="ri-upload-2-line" />
                            Trocar imagem
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleHomeServiceUpload(service.id, file);
                              }}
                            />
                          </label>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-dark/60 mb-1">Título</label>
                          <input
                            type="text"
                            value={service.title}
                            onChange={(e) => updateHomeServiceField(service.id, 'title', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-dark/60 mb-1">Slug da categoria</label>
                          <input
                            type="text"
                            value={service.category_slug}
                            onChange={(e) => updateHomeServiceField(service.id, 'category_slug', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-dark/60 mb-1">Descrição</label>
                          <textarea
                            value={service.description}
                            onChange={(e) => updateHomeServiceField(service.id, 'description', e.target.value)}
                            rows={3}
                            maxLength={200}
                            className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm text-dark placeholder:text-dark/30 focus:outline-none focus:border-dark/40 resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-8 md:p-10 max-w-sm w-full text-center shadow-2xl">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-5">
              <i className="ri-check-line text-3xl" />
            </div>
            <h3 className="font-serif text-xl text-dark mb-2">Upload concluído!</h3>
            <p className="text-dark/60 text-sm mb-6">
              Todas as fotos foram enviadas com sucesso.
            </p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-dark text-white rounded-full text-sm font-medium hover:bg-dark/90 transition-colors whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}